import React, { useState, Component } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Info, ChevronDown, RefreshCw, Check, Type, AlignLeft, Monitor, Smartphone, ArrowLeft, ArrowRight } from 'lucide-react';
export function ThemingPanel({
  onBack,
  onNext
}: {
  onBack?: () => void;
  onNext?: () => void;
}) {
  // State for Colour Overrides
  const [primaryOverride, setPrimaryOverride] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#722F37');
  const [accentOverride, setAccentOverride] = useState(false);
  const [accentColor, setAccentColor] = useState('#D4A574');
  const [textOverride, setTextOverride] = useState(false);
  const [textColor, setTextColor] = useState('#1A1A2E');
  const [bgOverride, setBgOverride] = useState(false);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  // State for Typography
  const [fontSource, setFontSource] = useState('inherit');
  const [headingWeight, setHeadingWeight] = useState('600');
  // State for Component Styling
  const [borderRadius, setBorderRadius] = useState('8px');
  const [buttonStyle, setButtonStyle] = useState('solid');
  const [cardShadow, setCardShadow] = useState('subtle');
  const [borderWeight, setBorderWeight] = useState('1px');
  // State for Custom Stylesheet
  const [customStyleMode, setCustomStyleMode] = useState('none');
  // State for Preview
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  // State for Block Settings
  const [activeSettingsTab, setActiveSettingsTab] = useState<'product-browser' | 'frequency-picker' | 'order-summary' | 'pack-selector'>('product-browser');
  const [displayOptions, setDisplayOptions] = useState({
    showPrice: true,
    showDiscount: true,
    showStock: true,
    showTags: true,
    enableSearch: true,
    enableFilter: true
  });
  // Frequency Picker state
  const [freqCardStyle, setFreqCardStyle] = useState('horizontal');
  const [freqHighlightBadge, setFreqHighlightBadge] = useState('best-value');
  const [freqDefaultSelection, setFreqDefaultSelection] = useState('best-value-frequency');
  const [freqDisplayOptions, setFreqDisplayOptions] = useState({
    showSavings: true,
    showNextDelivery: true,
    showDiscountPct: true,
    showTierName: false
  });
  // Order Summary state
  const [summaryStyle, setSummaryStyle] = useState('inline');
  const [ctaButtonText, setCtaButtonText] = useState('Subscribe & Pay');
  const [summaryDisplayOptions, setSummaryDisplayOptions] = useState({
    showThumbnails: true,
    showLineItemPricing: true,
    showTotalSavings: true,
    showShippingEstimate: false,
    editableQuantities: true,
    showNextBillingDate: false
  });
  // Pack Selector state
  const [packSelectorStyle, setPackSelectorStyle] = useState('cards-with-images');
  const [packDefaultSelection, setPackDefaultSelection] = useState('middle');
  const [packDisplayOptions, setPackDisplayOptions] = useState({
    showPackImage: true,
    showPackDescription: true,
    showPriceRange: true,
    showTierBadge: true,
    showBottleCount: false
  });
  const colorPresets = ['#722F37', '#D4A574', '#E2E8F0', '#F1F5F9', '#0F172A', '#FFFFFF'];
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-3577">
      {/* Section 1: Detected from Store */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3578">
        <div className="p-6" data-id="element-3579">
          <div className="flex items-center justify-between mb-4" data-id="element-3580">
            <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-3581">
              Detected from Store
            </h2>
            <div className="flex items-center gap-3" data-id="element-3582">
              <div className="flex items-center gap-1.5" data-id="element-3583">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" data-id="element-3584" />
                <span className="text-[12px] text-[#64748B]" data-id="element-3585">
                  Synced 2 min ago
                </span>
              </div>
              <button className="flex items-center gap-1 text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-3586">
                <RefreshCw className="w-3 h-3" data-id="element-3587" />
                Refresh
              </button>
            </div>
          </div>

          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3588">
            We read your active Shopify theme settings and product cards
            automatically. The widget inherits these by default.
          </p>

          <div className="flex items-start gap-4 mb-8" data-id="element-3589">
            <div className="w-16 h-16 bg-white border border-[#E2E8F0] rounded-lg flex items-center justify-center p-2" data-id="element-3590">
              <span className="text-2xl" data-id="element-3591">🍇</span>
            </div>
            <div data-id="element-3592">
              <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-3593">
                Oak Valley Estate Wines
              </h3>
              <p className="text-[13px] text-[#64748B]" data-id="element-3594">
                Theme: Dawn 15.0 (customised) · Published
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-4" data-id="element-3595">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3596">
              <div className="w-4 h-4 rounded-full bg-[#722F37]" data-id="element-3597" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3598">
                Primary
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3599">#722F37</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3600">
              <div className="w-4 h-4 rounded-full bg-[#D4A574]" data-id="element-3601" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3602">
                Accent
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3603">#D4A574</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3604">
              <div className="w-4 h-4 rounded-full bg-[#1A1A2E]" data-id="element-3605" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3606">
                Text
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3607">#1A1A2E</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3608">
              <div className="w-4 h-4 rounded-full bg-[#FFFDF8] border border-[#E2E8F0]" data-id="element-3609" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3610">
                Background
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3611">#FFFDF8</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-6" data-id="element-3612">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3613">
              <Type className="w-4 h-4 text-[#64748B]" data-id="element-3614" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3615">
                Headings
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3616">
                Playfair Display
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-3617">
              <AlignLeft className="w-4 h-4 text-[#64748B]" data-id="element-3618" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3619">
                Body
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-3620">
                Assistant (sans-serif)
              </span>
            </div>
          </div>

          <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-3621">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-3622" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-3623">
              Pulled from your theme's{' '}
              <code className="bg-[#E0F2FE] px-1 py-0.5 rounded text-[#0284C7] font-mono text-xs" data-id="element-3624">
                settings_data.json
              </code>{' '}
              and checkout branding API. Product cards in the widget use your
              theme's native product card markup where possible. Everything
              below is optional — only override what you want to change.
            </p>
          </div>
        </div>
      </Card>

      {/* Section 2: Colour Overrides */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3625">
        <div className="p-6" data-id="element-3626">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3627">
            Colour Overrides
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3628">
            Override specific colours from your theme. Anything left on
            "Inherit" uses the detected values above.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8" data-id="element-3629">
            {/* Primary Colour */}
            <div data-id="element-3630">
              <div className="flex items-center justify-between mb-3" data-id="element-3631">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3632">
                  Primary Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-3633">
                  <button onClick={() => setPrimaryOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!primaryOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3634">
                    Inherit
                  </button>
                  <button onClick={() => setPrimaryOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${primaryOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3635">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!primaryOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-3636">
                <div className="flex gap-3 mb-3" data-id="element-3637">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-3638">
                    <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: primaryColor
                  }} data-id="element-3639" />
                  </label>
                  <div className="flex-1 relative" data-id="element-3640">
                    <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3641" />
                  </div>
                </div>
                <div className="flex gap-2 mb-3" data-id="element-3642">
                  {colorPresets.slice(0, 4).map(color => <button key={color} onClick={() => setPrimaryColor(color)} className="w-8 h-6 rounded border border-[#E2E8F0]" style={{
                  backgroundColor: color
                }} data-id="element-3643" />)}
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-3644">
                  Buttons, active states, progress bar, badges
                </p>
              </div>
            </div>

            {/* Accent Colour */}
            <div data-id="element-3645">
              <div className="flex items-center justify-between mb-3" data-id="element-3646">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3647">
                  Accent Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-3648">
                  <button onClick={() => setAccentOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!accentOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3649">
                    Inherit
                  </button>
                  <button onClick={() => setAccentOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${accentOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3650">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!accentOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-3651">
                <div className="flex gap-3 mb-3" data-id="element-3652">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-3653">
                    <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: accentColor
                  }} data-id="element-3654" />
                  </label>
                  <div className="flex-1 relative" data-id="element-3655">
                    <input type="text" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3656" />
                  </div>
                </div>
                <div className="flex gap-2 mb-3" data-id="element-3657">
                  {colorPresets.slice(1, 5).map(color => <button key={color} onClick={() => setAccentColor(color)} className="w-8 h-6 rounded border border-[#E2E8F0]" style={{
                  backgroundColor: color
                }} data-id="element-3658" />)}
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-3659">
                  Discount badges, highlights, secondary actions
                </p>
              </div>
            </div>

            {/* Text Colour */}
            <div data-id="element-3660">
              <div className="flex items-center justify-between mb-3" data-id="element-3661">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3662">
                  Text Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-3663">
                  <button onClick={() => setTextOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!textOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3664">
                    Inherit
                  </button>
                  <button onClick={() => setTextOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${textOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3665">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!textOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-3666">
                <div className="flex gap-3 mb-3" data-id="element-3667">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-3668">
                    <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: textColor
                  }} data-id="element-3669" />
                  </label>
                  <div className="flex-1 relative" data-id="element-3670">
                    <input type="text" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3671" />
                  </div>
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-3672">
                  Headings and body text within the widget
                </p>
              </div>
            </div>

            {/* Background Colour */}
            <div data-id="element-3673">
              <div className="flex items-center justify-between mb-3" data-id="element-3674">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3675">
                  Background Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-3676">
                  <button onClick={() => setBgOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!bgOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3677">
                    Inherit
                  </button>
                  <button onClick={() => setBgOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${bgOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-3678">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!bgOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-3679">
                <div className="flex gap-3 mb-3" data-id="element-3680">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-3681">
                    <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: bgColor
                  }} data-id="element-3682" />
                  </label>
                  <div className="flex-1 relative" data-id="element-3683">
                    <input type="text" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3684" />
                  </div>
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-3685">
                  Widget card backgrounds. Set to transparent to inherit page
                  background.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 3: Typography Overrides */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3686">
        <div className="p-6" data-id="element-3687">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3688">
            Typography Overrides
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3689">
            By default the widget inherits your theme's fonts. Override here if
            the widget needs a different typeface.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3690">
            <div data-id="element-3691">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3692">
                Font Source
              </label>
              <div className="relative" data-id="element-3693">
                <select value={fontSource} onChange={e => setFontSource(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3694">
                  <option value="inherit" data-id="element-3695">Inherit from theme</option>
                  <option value="google" data-id="element-3696">Google Fonts</option>
                  <option value="custom" data-id="element-3697">Custom Font</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3698" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3699">
                Inherit uses whatever the merchant's Shopify theme specifies
              </p>
            </div>

            <div data-id="element-3700">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3701">
                Heading Weight
              </label>
              <div className="relative" data-id="element-3702">
                <select value={headingWeight} onChange={e => setHeadingWeight(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3703">
                  <option value="400" data-id="element-3704">Regular (400)</option>
                  <option value="500" data-id="element-3705">Medium (500)</option>
                  <option value="600" data-id="element-3706">Semi-bold (600)</option>
                  <option value="700" data-id="element-3707">Bold (700)</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3708" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3709">
                Weight applied to step titles and product names
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 4: Component Styling */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3710">
        <div className="p-6" data-id="element-3711">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3712">
            Component Styling
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3713">
            Shape and feel of interactive elements in the widget
          </p>

          {/* Border Radius */}
          <div className="mb-8" data-id="element-3714">
            <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3715">
              Border Radius
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3716">
              {[{
              id: '0px',
              label: 'Sharp (0px)',
              class: 'rounded-none'
            }, {
              id: '4px',
              label: 'Subtle (4px)',
              class: 'rounded-[4px]'
            }, {
              id: '8px',
              label: 'Rounded (8px)',
              class: 'rounded-[8px]'
            }, {
              id: '16px',
              label: 'Pill (16px)',
              class: 'rounded-[16px]'
            }].map(option => <div key={option.id} onClick={() => setBorderRadius(option.id)} className={`
                    flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all
                    ${borderRadius === option.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'}
                  `} data-id="element-3717">
                  <div className={`w-12 h-8 border border-[#94A3B8] ${option.class}`} data-id="element-3718" />

                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-3719">
                    {option.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Button Style */}
          <div className="mb-8" data-id="element-3720">
            <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3721">
              Button Style
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3722">
              {[{
              id: 'solid',
              label: 'Solid fill',
              preview: <div className="px-3 py-1.5 bg-[#722F37] text-white text-xs font-medium" style={{
                borderRadius: borderRadius
              }} data-id="element-3723">
                      Add to box
                    </div>
            }, {
              id: 'outline',
              label: 'Outline',
              preview: <div className="px-3 py-1.5 border border-[#722F37] text-[#722F37] text-xs font-medium" style={{
                borderRadius: borderRadius
              }} data-id="element-3724">
                      Add to box
                    </div>
            }, {
              id: 'rounded-fill',
              label: 'Rounded fill',
              preview: <div className="px-3 py-1.5 bg-[#722F37] text-white text-xs font-medium rounded-full" data-id="element-3725">
                      Add to box
                    </div>
            }, {
              id: 'rounded-outline',
              label: 'Rounded outline',
              preview: <div className="px-3 py-1.5 border border-[#722F37] text-[#722F37] text-xs font-medium rounded-full" data-id="element-3726">
                      Add to box
                    </div>
            }].map(option => <div key={option.id} onClick={() => setButtonStyle(option.id)} className={`
                    flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all
                    ${buttonStyle === option.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'}
                  `} data-id="element-3727">
                  {option.preview}
                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-3728">
                    {option.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3729">
            <div data-id="element-3730">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3731">
                Card Shadow
              </label>
              <div className="relative" data-id="element-3732">
                <select value={cardShadow} onChange={e => setCardShadow(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3733">
                  <option value="none" data-id="element-3734">None</option>
                  <option value="subtle" data-id="element-3735">Subtle</option>
                  <option value="medium" data-id="element-3736">Medium</option>
                  <option value="strong" data-id="element-3737">Strong</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3738" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3739">
                Shadow intensity on product cards and frequency options
              </p>
            </div>

            <div data-id="element-3740">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3741">
                Border Weight
              </label>
              <div className="relative" data-id="element-3742">
                <select value={borderWeight} onChange={e => setBorderWeight(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3743">
                  <option value="0px" data-id="element-3744">None (0px)</option>
                  <option value="1px" data-id="element-3745">Thin (1px)</option>
                  <option value="2px" data-id="element-3746">Medium (2px)</option>
                  <option value="3px" data-id="element-3747">Thick (3px)</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3748" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3749">
                Border thickness on cards and interactive elements
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 5: Custom Stylesheet */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3750">
        <div className="p-6" data-id="element-3751">
          <div className="flex items-center justify-between mb-4" data-id="element-3752">
            <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-3753">
              Custom Stylesheet
            </h2>
            <span className="px-2 py-0.5 bg-[#F3E8FF] text-[#7C3AED] text-[11px] font-bold rounded uppercase tracking-wider" data-id="element-3754">
              Agency
            </span>
          </div>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3755">
            For agencies: replace the default widget styles entirely with your
            own CSS. No overrides, no fragility.
          </p>

          <div className="flex border border-[#E2E8F0] rounded-lg overflow-hidden mb-6" data-id="element-3756">
            {[{
            id: 'none',
            label: 'No custom styles'
          }, {
            id: 'external',
            label: 'External URL'
          }, {
            id: 'inline',
            label: 'Inline CSS'
          }].map(mode => <button key={mode.id} onClick={() => setCustomStyleMode(mode.id)} className={`
                  flex-1 py-2.5 text-[13px] font-medium transition-colors
                  ${customStyleMode === mode.id ? 'bg-[#F0F9FF] text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'bg-white text-[#64748B] hover:bg-[#F8FAFC] border-b-2 border-transparent'}
                `} data-id="element-3757">
                {mode.label}
              </button>)}
          </div>

          {customStyleMode === 'none' && <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-3758">
              <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-3759" />
              <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-3760">
                The widget uses the brand colours, typography, and component
                styling configured above. This works for most merchants.
              </p>
            </div>}

          {customStyleMode === 'external' && <div data-id="element-3761">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3762">
                Stylesheet URL
              </label>
              <input type="text" placeholder="https://example.com/styles/widget.css" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3763" />
            </div>}

          {customStyleMode === 'inline' && <div data-id="element-3764">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3765">
                CSS
              </label>
              <textarea rows={6} placeholder=".ocean-widget { ... }" className="w-full p-3 border border-[#CBD5E1] rounded-lg text-[14px] font-mono text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3766" />
            </div>}
        </div>
      </Card>

      {/* Section 6: Block Settings */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3767">
        <div className="p-6" data-id="element-3768">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3769">
            Block Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3770">
            Per-block configuration for the storefront widget. Select a block to
            see its settings.
          </p>

          {/* Sub-tabs */}
          <div className="flex border-b border-[#E2E8F0] mb-6" data-id="element-3771">
            {([{
            id: 'product-browser',
            label: 'Product Browser'
          }, {
            id: 'frequency-picker',
            label: 'Frequency Picker'
          }, {
            id: 'order-summary',
            label: 'Order Summary'
          }, {
            id: 'pack-selector',
            label: 'Pack Selector'
          }] as const).map(tab => <button key={tab.id} onClick={() => setActiveSettingsTab(tab.id)} className={`text-[13px] font-semibold px-[18px] py-3 border-b-2 transition-colors duration-200 ${activeSettingsTab === tab.id ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#94A3B8] hover:text-[#64748B]'}`} data-id="element-3772">
                {tab.label}
              </button>)}
          </div>

          {activeSettingsTab === 'product-browser' && <div data-id="element-3773">
              <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3774">
                Display Options
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3775">
                {[{
              key: 'showPrice',
              label: 'Show price per bottle',
              desc: 'Individual pricing beneath each product'
            }, {
              key: 'showDiscount',
              label: 'Show discount badge',
              desc: 'Badge showing "10% off" per the selected frequency'
            }, {
              key: 'showStock',
              label: 'Show stock indicator',
              desc: 'Low stock or sold out labels'
            }, {
              key: 'showTags',
              label: 'Show product tags',
              desc: 'Varietal, region, or vintage tags on cards'
            }, {
              key: 'enableSearch',
              label: 'Enable search',
              desc: 'Search bar at top of product grid'
            }, {
              key: 'enableFilter',
              label: 'Enable filter tabs',
              desc: 'Tabs to filter by type (Red, White, Rosé, etc.)'
            }].map(({
              key,
              label,
              desc
            }) => <div key={key} onClick={() => setDisplayOptions(prev => ({
              ...prev,
              [key]: !prev[key as keyof typeof prev]
            }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${displayOptions[key as keyof typeof displayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3776">
                    <div className="pt-0.5 flex-shrink-0" data-id="element-3777">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${displayOptions[key as keyof typeof displayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3778">
                        {displayOptions[key as keyof typeof displayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3779" />}
                      </div>
                    </div>
                    <div data-id="element-3780">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3781">
                        {label}
                      </div>
                      <div className="text-[13px] text-[#64748B]" data-id="element-3782">{desc}</div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {activeSettingsTab === 'frequency-picker' && <div className="space-y-6" data-id="element-3783">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3784">
                <div data-id="element-3785">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3786">
                    Card Style
                  </label>
                  <div className="relative" data-id="element-3787">
                    <select value={freqCardStyle} onChange={e => setFreqCardStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3788">
                      <option value="horizontal" data-id="element-3789">Horizontal cards</option>
                      <option value="vertical" data-id="element-3790">Vertical cards</option>
                      <option value="compact" data-id="element-3791">Compact radio list</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3792" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3793">
                    How frequency options are displayed in the widget
                  </p>
                </div>
                <div data-id="element-3794">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3795">
                    Highlight Badge
                  </label>
                  <div className="relative" data-id="element-3796">
                    <select value={freqHighlightBadge} onChange={e => setFreqHighlightBadge(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3797">
                      <option value="none" data-id="element-3798">None</option>
                      <option value="best-value" data-id="element-3799">Best value</option>
                      <option value="most-popular" data-id="element-3800">Most popular</option>
                      <option value="recommended" data-id="element-3801">Recommended</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3802" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3803">
                    Automatically badge the frequency with the highest discount
                  </p>
                </div>
              </div>
              <div data-id="element-3804">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3805">
                  Default Selection
                </label>
                <div className="relative" data-id="element-3806">
                  <select value={freqDefaultSelection} onChange={e => setFreqDefaultSelection(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3807">
                    <option value="first-frequency" data-id="element-3808">First frequency</option>
                    <option value="best-value-frequency" data-id="element-3809">
                      Best value frequency
                    </option>
                    <option value="most-frequent" data-id="element-3810">Most frequent option</option>
                    <option value="none" data-id="element-3811">None — force a choice</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3812" />
                </div>
                <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3813">
                  Which frequency is pre-selected when the step loads
                </p>
              </div>
              <div data-id="element-3814">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3815">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3816">
                  {[{
                key: 'showSavings',
                label: 'Show savings amount',
                desc: 'Display "Save $XX per shipment" alongside the percentage'
              }, {
                key: 'showNextDelivery',
                label: 'Show next delivery date',
                desc: 'Estimated first delivery below each option'
              }, {
                key: 'showDiscountPct',
                label: 'Show discount percentage',
                desc: 'Display the discount percentage on each frequency card'
              }, {
                key: 'showTierName',
                label: 'Show tier name',
                desc: 'Display the loyalty tier earned at each frequency'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setFreqDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${freqDisplayOptions[key as keyof typeof freqDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3817">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-3818">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${freqDisplayOptions[key as keyof typeof freqDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3819">
                          {freqDisplayOptions[key as keyof typeof freqDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3820" />}
                        </div>
                      </div>
                      <div data-id="element-3821">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3822">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-3823">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab === 'order-summary' && <div className="space-y-6" data-id="element-3824">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3825">
                <div data-id="element-3826">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3827">
                    Summary Style
                  </label>
                  <div className="relative" data-id="element-3828">
                    <select value={summaryStyle} onChange={e => setSummaryStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3829">
                      <option value="inline" data-id="element-3830">Inline list</option>
                      <option value="sidebar" data-id="element-3831">Sidebar panel</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3832" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3833">
                    How the order summary is displayed on the review step
                  </p>
                </div>
                <div data-id="element-3834">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3835">
                    CTA Button Text
                  </label>
                  <input type="text" value={ctaButtonText} onChange={e => setCtaButtonText(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3836" />
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3837">
                    Text on the final checkout button
                  </p>
                </div>
              </div>
              <div data-id="element-3838">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3839">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3840">
                  {[{
                key: 'showThumbnails',
                label: 'Show wine thumbnails',
                desc: 'Small product images in the summary list'
              }, {
                key: 'showLineItemPricing',
                label: 'Show line item pricing',
                desc: 'Price per wine and per-bottle in the review'
              }, {
                key: 'showTotalSavings',
                label: 'Show total savings',
                desc: '"You\'re saving $XX with your subscription" line'
              }, {
                key: 'showShippingEstimate',
                label: 'Show shipping estimate',
                desc: 'Estimated shipping cost or "Free shipping" on the summary'
              }, {
                key: 'editableQuantities',
                label: 'Editable quantities',
                desc: 'Allow changing wine quantities from the summary before checkout'
              }, {
                key: 'showNextBillingDate',
                label: 'Show next billing date',
                desc: 'When the first payment will be charged'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setSummaryDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3841">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-3842">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3843">
                          {summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3844" />}
                        </div>
                      </div>
                      <div data-id="element-3845">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3846">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-3847">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab === 'pack-selector' && <div className="space-y-6" data-id="element-3848">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3849">
                <div data-id="element-3850">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3851">
                    Selector Style
                  </label>
                  <div className="relative" data-id="element-3852">
                    <select value={packSelectorStyle} onChange={e => setPackSelectorStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3853">
                      <option value="cards-with-images" data-id="element-3854">
                        Cards with images
                      </option>
                      <option value="cards-without-images" data-id="element-3855">
                        Cards without images
                      </option>
                      <option value="compact-button-group" data-id="element-3856">
                        Compact button group
                      </option>
                      <option value="radio-list" data-id="element-3857">Radio list</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3858" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3859">
                    How pack size options are presented on Step 1
                  </p>
                </div>
                <div data-id="element-3860">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3861">
                    Default Selection
                  </label>
                  <div className="relative" data-id="element-3862">
                    <select value={packDefaultSelection} onChange={e => setPackDefaultSelection(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3863">
                      <option value="first" data-id="element-3864">First pack size</option>
                      <option value="middle" data-id="element-3865">Middle pack size</option>
                      <option value="largest" data-id="element-3866">Largest pack size</option>
                      <option value="none" data-id="element-3867">None — force a choice</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3868" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3869">
                    Which pack size is pre-selected when the page loads
                  </p>
                </div>
              </div>
              <div data-id="element-3870">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-3871">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3872">
                  {[{
                key: 'showPackImage',
                label: 'Show pack image',
                desc: 'Display the image uploaded in Pack Sizes'
              }, {
                key: 'showPackDescription',
                label: 'Show pack description',
                desc: 'Display the description set in Pack Sizes'
              }, {
                key: 'showPriceRange',
                label: 'Show price range',
                desc: 'Display "From $XX/shipment" per pack size'
              }, {
                key: 'showTierBadge',
                label: 'Show tier badge',
                desc: 'Display loyalty tier name (Bronze, Silver, Gold) on pack cards'
              }, {
                key: 'showBottleCount',
                label: 'Show bottle count',
                desc: 'Display the number of bottles in each pack'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setPackDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${packDisplayOptions[key as keyof typeof packDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3873">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-3874">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${packDisplayOptions[key as keyof typeof packDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3875">
                          {packDisplayOptions[key as keyof typeof packDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3876" />}
                        </div>
                      </div>
                      <div data-id="element-3877">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3878">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-3879">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab !== 'product-browser' && activeSettingsTab !== 'frequency-picker' && activeSettingsTab !== 'order-summary' && activeSettingsTab !== 'pack-selector' && <div className="py-12 text-center text-[#64748B] bg-[#F8FAFC] rounded-lg border border-dashed border-[#E2E8F0]" data-id="element-3880">
                <p data-id="element-3881">Settings for {activeSettingsTab} coming soon...</p>
              </div>}
        </div>
      </Card>

      {/* Section 7: Live Preview */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3882">
        <div className="p-6" data-id="element-3883">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3884">
            Live Preview
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3885">
            How your widget will look with the current theme settings
          </p>

          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-3886">
            <div className="border-b border-[#E2E8F0] bg-white p-3 flex items-center justify-between" data-id="element-3887">
              <span className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-wider" data-id="element-3888">
                PREVIEW
              </span>
              <div className="flex items-center gap-1" data-id="element-3889">
                <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-3890">
                  <Monitor className="w-4 h-4" data-id="element-3891" />
                </button>
                <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-3892">
                  <Smartphone className="w-4 h-4" data-id="element-3893" />
                </button>
              </div>
            </div>

            <div className="bg-[#F8FAFC] p-8" data-id="element-3894">
              <div className="flex flex-col md:flex-row gap-6" data-id="element-3895">
                {/* Product Card 1 */}
                <div className="bg-white rounded-lg p-4 flex-1" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-3896">
                  <div className="relative aspect-square bg-[#FDF2F8] rounded-md mb-4 flex items-center justify-center" data-id="element-3897">
                    <span className="text-4xl" data-id="element-3898">🍷</span>
                    <span className="absolute top-2 right-2 px-2 py-0.5 text-[11px] font-bold text-white rounded" style={{
                    backgroundColor: '#722F37'
                  }} data-id="element-3899">
                      10% off
                    </span>
                  </div>
                  <h3 className="text-[14px] text-[#0F172A] mb-1" style={{
                  fontWeight: headingWeight
                }} data-id="element-3900">
                    2023 Shiraz Reserve
                  </h3>
                  <div className="flex items-center gap-2 mb-4" data-id="element-3901">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3902">
                      $42.00
                    </span>
                    <span className="text-[12px] text-[#94A3B8] line-through" data-id="element-3903">
                      $46.50
                    </span>
                  </div>
                  <button className="w-full py-2 text-[13px] font-medium transition-opacity hover:opacity-90" style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF',
                  borderRadius: borderRadius
                }} data-id="element-3904">
                    Add to box
                  </button>
                </div>

                {/* Product Card 2 */}
                <div className="bg-white rounded-lg p-4 flex-1" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-3905">
                  <div className="relative aspect-square bg-[#FFFBEB] rounded-md mb-4 flex items-center justify-center" data-id="element-3906">
                    <span className="text-4xl" data-id="element-3907">🥂</span>
                    <span className="absolute top-2 right-2 px-2 py-0.5 text-[11px] font-bold text-white rounded" style={{
                    backgroundColor: '#722F37'
                  }} data-id="element-3908">
                      10% off
                    </span>
                  </div>
                  <h3 className="text-[14px] text-[#0F172A] mb-1" style={{
                  fontWeight: headingWeight
                }} data-id="element-3909">
                    2024 Pinot Grigio
                  </h3>
                  <div className="flex items-center gap-2 mb-4" data-id="element-3910">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3911">
                      $29.00
                    </span>
                    <span className="text-[12px] text-[#94A3B8] line-through" data-id="element-3912">
                      $32.00
                    </span>
                  </div>
                  <button className="w-full py-2 text-[13px] font-medium transition-opacity hover:opacity-90" style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF',
                  borderRadius: borderRadius
                }} data-id="element-3913">
                    Add to box
                  </button>
                </div>

                {/* Frequency Selector */}
                <div className="bg-white rounded-lg p-4 w-64 flex-shrink-0" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-3914">
                  <h3 className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-wider mb-3" data-id="element-3915">
                    FREQUENCY
                  </h3>
                  <div className="space-y-2" data-id="element-3916">
                    <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#CBD5E1]" data-id="element-3917">
                      <div className="flex items-center gap-3" data-id="element-3918">
                        <div className="w-4 h-4 rounded-full border border-[#CBD5E1]" data-id="element-3919" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3920">
                          Monthly
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#D4A574]" data-id="element-3921">
                        10%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border-2 border-[#722F37] rounded-md cursor-pointer bg-[#FFFDF8]" data-id="element-3922">
                      <div className="flex items-center gap-3" data-id="element-3923">
                        <div className="w-4 h-4 rounded-full border-[5px] border-[#722F37]" data-id="element-3924" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3925">
                          Quarterly
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#722F37]" data-id="element-3926">
                        15%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#CBD5E1]" data-id="element-3927">
                      <div className="flex items-center gap-3" data-id="element-3928">
                        <div className="w-4 h-4 rounded-full border border-[#CBD5E1]" data-id="element-3929" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-3930">
                          Biannual
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#D4A574]" data-id="element-3931">
                        20%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-2 pb-4" data-id="element-3932">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-3933">
          <ArrowLeft className="w-4 h-4" data-id="element-3934" />
          Flow
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-5 py-2 text-[14px] font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] rounded-[6px] transition-colors" data-id="element-3935">
          Review Selling Plans
          <ArrowRight className="w-4 h-4" data-id="element-3936" />
        </button>
      </div>
    </div>;
}